import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { displayClientName } from "../../utils/clientName";

const useStyles = makeStyles((theme) => ({
  itemRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    marginBottom: theme.spacing(1),
  },
  desc: { flex: 1, minWidth: 0 },
  small: { width: 110 },
  total: {
    width: 120,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    paddingTop: 10,
    fontWeight: 600,
  },
  totals: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginLeft: "auto",
    maxWidth: 280,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 0),
    fontVariantNumeric: "tabular-nums",
  },
  grand: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    borderTop: `2px solid ${theme.palette.text.primary}`,
    marginTop: theme.spacing(0.5),
    fontWeight: 700,
    fontSize: 16,
    fontVariantNumeric: "tabular-nums",
  },
  hint: { color: theme.palette.text.secondary, display: "block", marginTop: 4 },
}));

const EMPTY_ITEM = { description: "", quantity: 1, unitPrice: 0, discount: 0 };
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const money = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const ProposalModal = ({ open, onClose, proposalId, dealId, onSaved }) => {
  const classes = useStyles();
  const [form, setForm] = useState({
    clientId: "",
    dealId: dealId || "",
    title: "",
    validUntil: "",
    notes: "",
    terms: "",
  });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [clients, setClients] = useState([]);
  const [deals, setDeals] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/clients", { params: { pageSize: 200 } })
      .then((r) => setClients(r.data.clients || []))
      .catch(() => setClients([]));
    api.get("/deals", { params: { status: "open", pageSize: 200 } })
      .then((r) => setDeals(r.data.deals || r.data.records || []))
      .catch(() => setDeals([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!proposalId) {
      setForm({ clientId: "", dealId: dealId || "", title: "", validUntil: "", notes: "", terms: "" });
      setItems([{ ...EMPTY_ITEM }]);
      return;
    }
    api.get(`/proposals/${proposalId}`)
      .then(({ data }) => {
        setForm({
          clientId: data.clientId || "",
          dealId: data.dealId || "",
          title: data.title || "",
          validUntil: data.validUntil || "",
          notes: data.notes || "",
          terms: data.terms || "",
        });
        setItems(
          (data.items || []).length
            ? data.items.map((i) => ({
                description: i.description,
                quantity: Number(i.quantity),
                unitPrice: Number(i.unitPrice),
                discount: Number(i.discount),
              }))
            : [{ ...EMPTY_ITEM }]
        );
      })
      .catch(toastError);
  }, [open, proposalId, dealId]);

  const setItem = (idx, field) => (e) => {
    const value = field === "description" ? e.target.value : e.target.value;
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it))
    );
  };

  // Mesma regra do backend: desconto de linha nunca supera o bruto.
  const lineTotal = (it) => {
    const gross = round2((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0));
    const disc = Math.min(Math.max(0, Number(it.discount) || 0), gross);
    return round2(gross - disc);
  };
  const subtotal = round2(
    items.reduce((s, it) => s + round2((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0)), 0)
  );
  const discountTotal = round2(
    items.reduce((s, it) => {
      const gross = round2((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0));
      return s + Math.min(Math.max(0, Number(it.discount) || 0), gross);
    }, 0)
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        clientId: form.clientId ? Number(form.clientId) : null,
        dealId: form.dealId ? Number(form.dealId) : null,
        validUntil: form.validUntil || null,
        items: items
          .filter((i) => i.description.trim())
          .map((i) => ({
            description: i.description.trim(),
            quantity: Number(i.quantity) || 0,
            unitPrice: Number(i.unitPrice) || 0,
            discount: Number(i.discount) || 0,
          })),
      };
      if (proposalId) await api.put(`/proposals/${proposalId}`, payload);
      else await api.post("/proposals", payload);
      toast.success(proposalId ? "Proposta atualizada" : "Proposta criada como rascunho");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  }, [form, items, proposalId, onSaved, onClose]);

  const canSave =
    (form.clientId || form.dealId) &&
    items.some((i) => i.description.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{proposalId ? "Editar proposta" : "Nova proposta"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Negócio (opcional)"
              value={form.dealId}
              onChange={(e) => setForm({ ...form, dealId: e.target.value })}
            >
              <MenuItem value="">Sem vínculo</MenuItem>
              {deals.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.title}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" className={classes.hint}>
              Com negócio vinculado, o aceite move para ganho e gera o recebível.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Cliente *"
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
            >
              <MenuItem value="">—</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {displayClientName(c)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Título *"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="date"
              label="Válida até"
              InputLabelProps={{ shrink: true }}
              value={form.validUntil || ""}
              onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Itens
            </Typography>
            {items.map((it, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={idx} className={classes.itemRow}>
                <TextField
                  className={classes.desc}
                  label="Descrição"
                  value={it.description}
                  onChange={setItem(idx, "description")}
                />
                <TextField
                  className={classes.small}
                  label="Qtd"
                  type="number"
                  value={it.quantity}
                  onChange={setItem(idx, "quantity")}
                />
                <TextField
                  className={classes.small}
                  label="Unitário"
                  type="number"
                  value={it.unitPrice}
                  onChange={setItem(idx, "unitPrice")}
                />
                <TextField
                  className={classes.small}
                  label="Desconto"
                  type="number"
                  value={it.discount}
                  onChange={setItem(idx, "discount")}
                />
                <div className={classes.total}>{money(lineTotal(it))}</div>
                <IconButton
                  size="small"
                  disabled={items.length === 1}
                  onClick={() => setItems((p) => p.filter((_, i) => i !== idx))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setItems((p) => [...p, { ...EMPTY_ITEM }])}
            >
              Adicionar item
            </Button>

            <div className={classes.totals}>
              <div className={classes.row}>
                <span>Subtotal</span>
                <span>{money(subtotal)}</span>
              </div>
              {discountTotal > 0 && (
                <div className={classes.row}>
                  <span>Desconto</span>
                  <span>− {money(discountTotal)}</span>
                </div>
              )}
              <div className={classes.grand}>
                <span>Total</span>
                <span>{money(subtotal - discountTotal)}</span>
              </div>
            </div>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Observações"
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Condições comerciais"
              value={form.terms || ""}
              onChange={(e) => setForm({ ...form, terms: e.target.value })}
              helperText="Prazo, forma de pagamento, garantia — aparece na página do cliente."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" variant="contained" onClick={handleSave} disabled={saving || !canSave}>
          {saving ? "Salvando…" : "Salvar rascunho"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ProposalModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  proposalId: PropTypes.number,
  dealId: PropTypes.number,
  onSaved: PropTypes.func,
};

ProposalModal.defaultProps = {
  proposalId: null,
  dealId: null,
  onSaved: undefined,
};

export default ProposalModal;
